import { Badge, ListingState, ListingTag } from '__generated__/athena/graphql';
import type { MarketplaceProperty } from 'components/marketplace/property/types';

import { isBuyDirectEligible } from './buyDirectHelpers';

type TourKind = 'selfTour' | 'tourWithAgent';

export const TOUR_ACTION_TEXT_NON_BUY_DIRECT = {
  selfTour: 'Self-tour',
  tourWithAgent: 'Share home',
} satisfies Record<TourKind, string>;

export const TOUR_ACTION_TEXT_BUY_DIRECT = {
  selfTour: 'Self-tour',
  tourWithAgent: 'Tour with agent',
} satisfies Record<TourKind, string>;

export type PropertyActionabilityInput = Pick<
  MarketplaceProperty,
  | 'buyDirectPrice'
  | 'canSelfTour'
  | 'displayBadge'
  | 'hasCurrentAvailability'
  | 'listingState'
  | 'listingTags'
>;

export const determinePropertyActionability = (property: PropertyActionabilityInput) => {
  if (!property.hasCurrentAvailability) {
    return {
      isActionable: false,
      tourKind: null,
      showBuyDirect: false,
      disableBuyDirect: true,
    };
  }

  const isUnknownRequiresAgent = property.displayBadge === Badge.UNKNOWN && !property.canSelfTour;

  // Backend badge semantics pair ON_THE_MARKET with self-tour and TRADITIONAL
  // with an agent tour; UNKNOWN also requires an agent when self-tour is unavailable.
  const canTourWithAgent = property.displayBadge === Badge.TRADITIONAL || isUnknownRequiresAgent;

  const isActionable = property.displayBadge === Badge.ON_THE_MARKET || canTourWithAgent;

  const showBuyDirect = isBuyDirectEligible(property) && (property.buyDirectPrice ?? 0) > 0;

  const hasDisqualifyingTags =
    property.listingTags?.some((tag) => tag !== ListingTag.BUY_DIRECT_ELIGIBLE) ?? false;

  const disableBuyDirect =
    property.displayBadge === Badge.IN_CONTRACT ||
    property.listingState === ListingState.IN_CONTRACT ||
    hasDisqualifyingTags;

  let tourKind: TourKind | null = null;
  if (isActionable) {
    tourKind = canTourWithAgent ? 'tourWithAgent' : 'selfTour';
  }

  return {
    isActionable,
    tourKind,
    showBuyDirect,
    disableBuyDirect,
  };
};
